import PublicNav from "@/components/PublicNav";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";
import {
  CalendarDays, ListChecks, Sparkles, ClipboardList, Layers, Users,
  Bell, FileSpreadsheet, FileText, History, Smartphone,
  ShieldCheck, Wand2, Target, BarChart3, Upload, Bot, Zap,
} from "lucide-react";

// Phase 2Z.w-3 - Four-section restructure. Each section maps to a
// pricing anchor (#automation, #intelligence, #performance, #foundations).

const AUTOMATION = [
  { icon: Bot, title: "AI-assisted RMI Assistant", body: "Tell NextRota what you want to improve in plain English. It suggests a safe rota mix change for you to preview. Available in Command, and during the 14-day full Command trial and founder Command-level access." },
  { icon: Zap, title: "Autopilot foundation", body: "Manager-controlled, rules-based automation. NextRota prepares drafts and progresses them on schedules you define. Available today." },
  { icon: Wand2, title: "Auto-draft creation", body: "Drafts are created automatically based on your Rota Style and patterns, ready for managers to review." },
  { icon: ShieldCheck, title: "Review-window approval", body: "Drafts auto-approve after the review window you set. Managers can edit any time before that." },
  { icon: Sparkles, title: "Auto-publish when configured", body: "Approved drafts publish automatically when allowed, with full audit trail." },
  { icon: ListChecks, title: "Coverage-aware leave approval", body: "Clean leave can be auto-approved when your minimum coverage rules still hold." },
  { icon: Bell, title: "Manager and employee notifications", body: "Email on draft created, approved, published, and on leave decisions. Always under your rules." },
];

const INTELLIGENCE = [
  { icon: ShieldCheck, title: "Coverage Guard", body: "Checks every rota change against minimum-cover rules. Warns before any edit leaves a gap." },
  { icon: Wand2, title: "Rota Style", body: "Reusable cycle-day templates. Build the shape of a typical week once, then apply across teams and weeks." },
  { icon: Layers, title: "Pattern Engine", body: "Varies day-by-day assignment patterns across active cells, keeping shift times, breaks and empty cells fixed." },
  { icon: Sparkles, title: "Rota Mix Intelligence", body: "Rebalance the team's category mix safely. Preview the impact on coverage before applying." },
  { icon: Sparkles, title: "RMI scope detection", body: "Mentions of a team, employee or weekday in your prompt pre-fill Refine Scope. Manager can still adjust." },
];

const PERFORMANCE = [
  { icon: Target, title: "Performance Focus", body: "Compare employee activity results against the work assigned on the rota." },
  { icon: BarChart3, title: "Performance Focus dashboard", body: "Company, team and employee views. Achievement, gaps, top performers and needs-attention at a glance." },
  { icon: ShieldCheck, title: "Leave-adjusted target fairness", body: "Expected targets reduce for employees on leave or bank holiday. Actuals stay as recorded." },
  { icon: Upload, title: "CSV and paste import", body: "Bring in activity results from Excel or Google Sheets. Header rows auto-detected. UK date formats accepted." },
  { icon: ClipboardList, title: "Utilisation reports", body: "Company, team and employee reports. Working time, category mix, leave and bank holidays." },
];

const FOUNDATIONS = [
  { icon: CalendarDays, title: "Roster planning", body: "Visual weekly grid, copy-from-last-week, conflict warnings, locked-week governance." },
  { icon: Users, title: "Teams and people", body: "Sub-teams, team admins, scoped permissions, employee read-only roster." },
  { icon: ListChecks, title: "Leave and swaps", body: "Half-day support, approvals, automatic capacity blocking, bank-holiday awareness." },
  { icon: Sparkles, title: "Skills and employee preferences", body: "Tag people with skills and surface them at scheduling time." },
  { icon: FileSpreadsheet, title: "Excel export", body: "Share-ready workbook with banner, day super-headers, freeze panes and totals." },
  { icon: FileText, title: "PDF export", body: "Landscape PDF, branded, ready to print or share." },
  { icon: History, title: "Audit history", body: "Every assignment, approval and bulk action recorded. Search and filter by user or action." },
  { icon: Smartphone, title: "Mobile access", body: "Mobile drawer navigation, swipe-friendly roster, employee read-only view." },
];

const SOON = [
  "Salesforce connector and generic import API",
  "Undo import and batch rollback",
  "Period-comparison sparklines on the dashboard",
  "Multi-site readiness",
  "Advanced automation expansion",
];

function FeatureGrid({ items, testId }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-4" data-testid={testId}>
      {items.map(({ icon: Icon, title, body }) => (
        <div key={title} className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="h-10 w-10 rounded-lg bg-[#DBE7FE] text-[#1D4ED8] grid place-items-center mb-3">
            <Icon className="h-5 w-5"/>
          </div>
          <div className="font-display text-lg text-[#0B1A36]">{title}</div>
          <p className="text-sm text-[#4B5773] mt-1.5">{body}</p>
        </div>
      ))}
    </div>
  );
}

function SectionHead({ id, eyebrow, title, body }) {
  return (
    <div id={id} className="mt-16 scroll-mt-24" data-testid={`features-section-${id}`}>
      <div className="inline-block px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 text-[10px] uppercase tracking-widest font-semibold">
        {eyebrow}
      </div>
      <h2 className="font-display text-2xl sm:text-3xl text-[#0B1A36] mt-2">{title}</h2>
      {body && <p className="text-sm text-[#4B5773] mt-2 max-w-2xl">{body}</p>}
    </div>
  );
}

export default function FeaturesPage() {
  return (
    <div className="min-h-screen flex flex-col bg-white overflow-x-hidden">
      <PublicNav />

      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <div className="text-center max-w-2xl mx-auto">
          <div className="eyebrow text-[#4B5773]">Features</div>
          <h1 className="font-display text-4xl sm:text-5xl text-[#0B1A36] mt-2">Less rota admin. More control.</h1>
          <p className="text-base text-[#4B5773] mt-4">Available now and planned next. Every feature here is something NextRota does today, unless flagged as on the roadmap.</p>
        </div>

        {/* Plan anchor: Starter maps to core foundations. */}
        <div id="starter" className="scroll-mt-24" data-testid="features-anchor-starter" aria-hidden="true" />
        <SectionHead
          id="foundations"
          eyebrow="Starter plan"
          title="Core rota foundations"
          body="The everyday tools your team uses week in, week out. Included in every plan, starting with Starter." />
        <FeatureGrid items={FOUNDATIONS} testId="features-foundations-grid" />

        {/* Plan anchor: Growth maps to smart coverage and performance. */}
        <div id="growth" className="scroll-mt-24" data-testid="features-anchor-growth" aria-hidden="true" />
        <SectionHead
          id="intelligence"
          eyebrow="Growth plan"
          title="Smart coverage and rota intelligence"
          body="Coverage Guard, Rota Style, Pattern Engine, Rota Mix Intelligence and RMI scope detection. Preview before applying, always." />
        <FeatureGrid items={INTELLIGENCE} testId="features-intelligence-grid" />

        <SectionHead
          id="performance"
          eyebrow="Included in Growth"
          title="Performance Focus, included in Growth"
          body="Compare activity results against the work you assigned. Targets adjust fairly for leave and bank holidays." />
        <FeatureGrid items={PERFORMANCE} testId="features-performance-grid" />

        {/* Plan anchor: Command maps to automation and Autopilot. */}
        <div id="command" className="scroll-mt-24" data-testid="features-anchor-command" aria-hidden="true" />
        <SectionHead
          id="automation"
          eyebrow="Command plan"
          title="Manager-controlled automation"
          body="The Autopilot module is live and included in the Command plan. Every 14-day trial includes full Command access, so you can try Automation before choosing a plan. Configure rules so NextRota prepares drafts, approves and publishes when allowed, and supports leave decisions based on coverage. Managers stay in control, automation is rules-based and coverage-aware." />
        <FeatureGrid items={AUTOMATION} testId="features-today-grid" />

        <div className="mt-16 inline-block px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 text-[10px] uppercase tracking-widest font-semibold">
          On the roadmap
        </div>
        <h2 className="font-display text-2xl sm:text-3xl text-[#0B1A36] mt-2">Planned next</h2>
        <p className="text-sm text-[#4B5773] mt-2 max-w-2xl">
          Genuinely future. Founding customers help shape the order these ship.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-6" data-testid="features-soon-grid">
          {SOON.map(item => (
            <div key={item} className="rounded-lg border border-dashed border-slate-300 bg-white px-4 py-3 text-sm text-[#1F2A44] flex items-start gap-2">
              <span className="mt-0.5 text-[9px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">Soon</span>
              <span>{item}</span>
            </div>
          ))}
        </div>

        <div className="mt-16 text-center">
          <Link to="/start" data-testid="features-cta"
            className="inline-flex items-center px-6 py-3 rounded-md bg-[#2563EB] text-white font-semibold hover:bg-[#1D4ED8]">
            Start your 14-day free trial
          </Link>
          <p className="text-xs text-[#4B5773] mt-3">14-day free trial, no credit card required. Cancel anytime.</p>
          <p className="text-xs text-[#4B5773] mt-1">First 10 founding companies get 3 months free.</p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
